// bluaIQ — NBA-2: "Ask for clarification / Pedir aclaración" draft builder.
//
// FE-only, pure (no React, no fetch, no persistence) so it can be unit-tested
// directly. Produces a reviewable clarification-request DRAFT (subject + body)
// the builder can copy / edit before contacting the contractor. NOTHING here
// sends an email, creates a contact, or writes to any module.
//
// HARD RULES (mirror the NBA-2 spec):
//   • The draft is generated from the bid's MISSING / unclear information only
//     (the same NBA-1 decision signals already shown in the decision panel).
//   • It never invents warranty / payment / schedule / scope details — every
//     bullet is a REQUEST to clarify, phrased as a question, not a stated fact.
//   • Conditional fields degrade honestly: missing contractor name → neutral
//     salutation; missing project name → the project clause is omitted.
//   • The signature is a bracketed placeholder the user fills in — we do not
//     invent the user's name or company.

export interface ClarificationDraftInput {
  contractorName?: string | null
  /** Already-localized trade label, e.g. "Plumbing" / "Plomería". */
  tradeName?: string | null
  projectName?: string | null
  /** NBA-1 derived missing-info keys: scope | payment_terms | warranty | schedule. */
  missingFields?: string[]
  /** Extra clarification topic keys derived from AI red-flag categories. */
  extraTopics?: string[]
}

export interface ClarificationDraft {
  subject: string
  body: string
  /** Ordered, de-duplicated topic keys the draft asked about. */
  topicKeys: string[]
  /** False when the bid had no missing/unclear info (generic draft offered). */
  hasMissingInfo: boolean
}

// A placeholder contractor name we must NOT address the contractor by — it is
// the server's fallback when the AI could not read a real name.
const UNKNOWN_CONTRACTOR = 'Unknown Contractor'

// Bilingual clarification bullets, keyed by topic. Each is a QUESTION / request
// — never an assertion about what the bid contains.
const TOPIC_BULLETS: Record<string, { en: string; es: string }> = {
  scope: {
    en: 'Confirm the full scope of work included, and list anything excluded.',
    es: 'Confirma el alcance completo del trabajo incluido y enumera cualquier exclusión.',
  },
  payment_terms: {
    en: 'Provide the payment terms: deposit amount/percentage and the draw/payment schedule.',
    es: 'Indica las condiciones de pago: monto/porcentaje de depósito y el calendario de pagos.',
  },
  warranty: {
    en: 'Specify the warranty period offered (in months).',
    es: 'Especifica el período de garantía ofrecido (en meses).',
  },
  schedule: {
    en: 'Provide the estimated duration and the start / completion dates.',
    es: 'Indica la duración estimada y las fechas de inicio / finalización.',
  },
  insurance: {
    en: 'Confirm general liability (GL) and workers’ compensation insurance, plus license number.',
    es: 'Confirma el seguro de responsabilidad general (GL), la compensación laboral (workers’ compensation) y el número de licencia.',
  },
  pricing: {
    en: 'Clarify the pricing so each line/option is unambiguous (which items are included in the total).',
    es: 'Aclara el precio para que cada partida/opción sea inequívoca (qué conceptos incluye el total).',
  },
  materials_fixtures: {
    en: 'Confirm which materials and fixtures are included vs. excluded.',
    es: 'Confirma qué materiales y accesorios están incluidos y cuáles excluidos.',
  },
  expiration: {
    en: 'Confirm how long this bid is valid (the expiration date).',
    es: 'Confirma por cuánto tiempo es válida esta propuesta (la fecha de expiración).',
  },
}

// Stable display order for topics regardless of input order.
const TOPIC_ORDER = [
  'scope',
  'pricing',
  'payment_terms',
  'schedule',
  'warranty',
  'materials_fixtures',
  'insurance',
  'expiration',
]

/**
 * Build the bilingual clarification draft for a single bid.
 *
 * Returns the subject + body for the requested language plus the topic keys
 * that were used (so the UI can show "which missing items were used"). The
 * caller decides what to do with it — copy, edit, or nothing. No side effects.
 */
export function buildClarificationDraft(input: ClarificationDraftInput, isEs: boolean): ClarificationDraft {
  // Merge + de-dupe topic keys, keep only ones we have copy for, order stably.
  const requested = new Set<string>([...(input.missingFields ?? []), ...(input.extraTopics ?? [])])
  const topicKeys = TOPIC_ORDER.filter(k => requested.has(k) && TOPIC_BULLETS[k])
  const hasMissingInfo = topicKeys.length > 0

  const rawName = (input.contractorName ?? '').trim()
  const hasName = rawName !== '' && rawName !== UNKNOWN_CONTRACTOR
  const trade = (input.tradeName ?? '').trim()
  const project = (input.projectName ?? '').trim()

  // ── Subject ────────────────────────────────────────────────────────────
  // "Clarification request — {trade} proposal for {project}" with the trade
  // and project clauses dropped when unknown.
  let subject: string
  if (isEs) {
    const tradePart = trade ? `propuesta de ${trade}` : 'propuesta'
    subject = `Solicitud de aclaración — ${tradePart}${project ? ` para ${project}` : ''}`
  } else {
    const tradePart = trade ? `${trade} proposal` : 'proposal'
    subject = `Clarification request — ${tradePart}${project ? ` for ${project}` : ''}`
  }

  // ── Bullets ────────────────────────────────────────────────────────────
  const bullets = topicKeys
    .map(k => `- ${isEs ? TOPIC_BULLETS[k].es : TOPIC_BULLETS[k].en}`)
    .join('\n')

  // ── Body ───────────────────────────────────────────────────────────────
  const salutation = isEs
    ? (hasName ? `Hola ${rawName},` : 'Hola,')
    : (hasName ? `Hi ${rawName},` : 'Hi,')

  const thanksLine = isEs
    ? (project ? `Gracias por enviar tu propuesta para ${project}.` : 'Gracias por enviar tu propuesta.')
    : (project ? `Thank you for your proposal for ${project}.` : 'Thank you for your proposal.')

  // Closing signature is a bracketed placeholder — we never invent the user's
  // name or company.
  const signature = isEs ? 'Gracias,\n[Tu nombre / empresa]' : 'Thank you,\n[Your name / company]'

  let body: string
  if (hasMissingInfo) {
    const lead = isEs
      ? 'Antes de poder comparar o seleccionar esta propuesta, necesitamos aclarar los siguientes puntos:'
      : 'Before we can compare or select this bid, please clarify the following items:'
    const confirmLine = isEs
      ? 'Por favor confirma también si el precio enviado incluye toda la mano de obra, materiales, exclusiones, garantía, calendario y condiciones de pago para el alcance indicado.'
      : 'Please also confirm whether the submitted price includes all labor, materials, exclusions, warranty, schedule, and payment terms for the listed scope.'
    body = `${salutation}\n\n${thanksLine}\n\n${lead}\n\n${bullets}\n\n${confirmLine}\n\n${signature}`
  } else {
    // No missing info detected — offer a generic confirmation draft (per spec:
    // "say no clarification is needed or offer a generic clarification draft").
    const genericLine = isEs
      ? 'La propuesta parece completa. Para confirmar antes de comparar o seleccionar, por favor verifica que el precio enviado incluye toda la mano de obra, materiales, exclusiones, garantía, calendario y condiciones de pago para el alcance indicado.'
      : 'This proposal looks complete. To confirm before we compare or select it, please verify that the submitted price includes all labor, materials, exclusions, warranty, schedule, and payment terms for the listed scope.'
    body = `${salutation}\n\n${thanksLine}\n\n${genericLine}\n\n${signature}`
  }

  return { subject, body, topicKeys, hasMissingInfo }
}
